import type { OpsFilterOption } from "./opsDashboard.types";

/**
 * Selection rules for a hierarchical filter, matching OrganizationMultiPicker.
 *
 * Ticking a parent ticks the children beneath it, so the selection shows what the filter
 * is actually going to match — the backend matches down the ancestor chain, and a parent
 * ticked while its children sit unticked reads as though only the parent's own rows are
 * included.
 *
 * Unticking a child unticks its ancestors too. Without that, clearing one campus from a
 * selected system would change nothing: the system is still ticked, so the backend still
 * matches the campus through it, and the checkbox would silently spring back in meaning
 * if not in appearance.
 *
 * The tree comes from the flattened depth-first order the backend sends, so the parent of
 * an option is the nearest one above it with a smaller depth. Options with no depth — Ops
 * Owner, CSM — have no relations, and this reduces to a plain toggle.
 */
export function toggleInTree(
    options: OpsFilterOption[],
    selected: number[],
    id: number,
): number[] {
    const index = options.findIndex((o) => o.id === id);
    if (index === -1) return selected;

    const next = new Set(selected);
    const depth = (o: OpsFilterOption) => o.depth ?? 0;

    const descendants: number[] = [];
    for (let i = index + 1; i < options.length; i++) {
        if (depth(options[i]) <= depth(options[index])) break;
        descendants.push(options[i].id);
    }

    const ancestors: number[] = [];
    let above = depth(options[index]);
    for (let i = index - 1; i >= 0 && above > 0; i--) {
        if (depth(options[i]) < above) {
            ancestors.push(options[i].id);
            above = depth(options[i]);
        }
    }

    if (next.has(id)) {
        next.delete(id);
        descendants.forEach((d) => next.delete(d));
        ancestors.forEach((a) => next.delete(a));
    } else {
        next.add(id);
        descendants.forEach((d) => next.add(d));
    }

    // Keep the caller's order stable rather than returning set order.
    return options.filter((o) => next.has(o.id)).map((o) => o.id);
}
